'use client';

// Подтверждение («Точно выйти?», «Точно удалить?») — на ОБЩЕМ нижнем листе кабинета
// (bottom-sheet.tsx; владелец 2026-07-26: «эту шторку отдельным компонентом — и для всех
// уточнений, и для темы»). Показывается ТОЛЬКО В ПРИЛОЖЕНИИ (владелец 2026-07-25: «в
// мобильном пусть инлайн пока останется»), в браузере ConfirmInline рисует прежний
// вопрос + [Да] [Нет] в строке.
//
// Вид родной для iPhone: вопрос тонким шрифтом по центру, кнопка действия во всю ширину
// своим словом («Удалить», «Выйти») и «Отмена»; сверху крестик закрытия.
import { type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { BottomSheet } from './bottom-sheet';
import { useSlideStack } from './native-layer-host';
import { nativeBridge, nativeMethod, readAccentRgb } from '@/lib/native-app';
import { KBareButton } from '@scriptera/ui/kit';

export interface ConfirmAsk {
  /** Вопрос («Точно удалить?») — крупно и по центру. */
  question: ReactNode;
  /** Одна поясняющая строка под вопросом («Используется ещё в 2 местах», правило 16
   *  владельца 19.08.2026). Нет строки — окно как раньше, без неё. */
  note?: ReactNode;
  /** Слово на кнопке действия («Удалить», «Выйти»). */
  confirmLabel: ReactNode;
  /** true → кнопка действия красная (удаление/выход); false → акцентная. */
  danger?: boolean;
  /** Обе капсулы серые — вопрос без «главного» действия («Точно выйти?» в iOS, кадр A09). */
  neutral?: boolean;
  onConfirm: () => void;
}

export function ConfirmSheet({ ask, onClose }: { ask: ConfirmAsk; onClose: () => void }) {
  const t = useTranslations('cabinet');
  return (
    <BottomSheet onClose={onClose}>
      {(close) => (
        <div className="cfm">
          <div className="cfm-q">
            <span>
              {ask.question}
              {ask.note ? <span className="cfm-note">{ask.note}</span> : null}
            </span>
          </div>
          <div className="cfm-acts">
            <KBareButton
              type="button"
              className={`cfm-btn${ask.danger === false ? ' primary' : ' danger'}`}
              onClick={() => {
                close();
                ask.onConfirm();
              }}
            >
              {ask.confirmLabel}
            </KBareButton>
            <KBareButton type="button" className="cfm-btn cfm-cancel" onClick={close}>
              {t('cancel')}
            </KBareButton>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}

/** Показывать ли лист вместо inline-вопроса: только в приложении (в браузере — прежний вид). */
export function useSheetConfirm(): boolean {
  return useSlideStack().available;
}

/** Есть ли РОДНОЕ подтверждение iOS в этой сборке приложения (проверять ТОЛЬКО перед вызовом,
 *  а не в рендере: мост появляется после старта, а SSR о нём не знает). */
export function hasNativeConfirm(): boolean {
  return nativeMethod('confirm');
}

/** Есть ли РОДНОЕ ОКНО-СООБЩЕНИЕ в этой сборке (волна 3, метод моста `notice`). Проверять
 *  ТОЛЬКО перед вызовом: мост появляется после старта, а SSR о нём не знает. */
export function hasNativeNotice(): boolean {
  return nativeMethod('notice');
}

/**
 * ПОКАЗАТЬ РОДНОЕ ОКНО-СООБЩЕНИЕ (волна 3) — одна рука на весь кабинет.
 *
 * Две подачи, обе повторяют веб один в один: `sheet` — низкий лист с заголовком и
 * текстом (веб: panel.open со `sheet: true, sheetDetent: 0.4`), `card` — карточка по
 * центру со значком и двумя кнопками (веб: `.push-ask`/`.push-card`). Возвращает
 * «нажали первую кнопку»; сборка без метода — сразу false, и звонящий показывает
 * прежнее веб-окно.
 */
export function showNativeNotice(opts: {
  style: 'sheet' | 'card';
  /** SF-символ значка карточки (у листа значка нет). */
  icon?: string;
  title: string;
  text: string;
  primary?: string;
  secondary?: string;
  /** ВИБРАЦИЯ ОТКАЗА в момент показа окна (владелец 10.09.2026): 'error' — системный
   *  отклик ошибки iOS. Ставится там, где окно объясняет ОТКАЗ («оставьте хотя бы один
   *  способ»): человек обязан почувствовать, что действие не прошло. Старые сборки ключ
   *  не знают и просто показывают окно. */
  haptic?: 'error';
}): Promise<boolean> {
  const br = nativeBridge();
  if (!br?.notice || !nativeMethod('notice')) return Promise.resolve(false);
  return br
    .notice({
      ...opts,
      theme: document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
      ...readAccentRgb(),
    })
    .then((r) => !!r?.ok)
    .catch(() => false);
}

/** То же окно, но с ответом целиком: `{ok, choice?}` (choice — сборка 251+, у 1.1 его нет). Нужен там, где
 *  вторая кнопка и закрытие значат разное (UT 14.10 п.26). Мост промолчал/сломался — `{ok:false}`. */
export function showNativeNoticeFull(
  opts: Parameters<typeof showNativeNotice>[0],
): Promise<{ ok: boolean; choice?: 'primary' | 'secondary' | 'close' }> {
  const br = nativeBridge();
  if (!br?.notice || !nativeMethod('notice')) return Promise.resolve({ ok: false });
  return br
    .notice({
      ...opts,
      theme: document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
      ...readAccentRgb(),
    })
    .then((r) => ({ ok: !!r?.ok, ...(r?.choice ? { choice: r.choice } : {}) }))
    .catch(() => ({ ok: false }));
}

/** Спросить системным окном iOS (владелец 2026-07-26). Вид кабинета уезжает в натив, чтобы
 *  окно не было светлым над тёмным кабинетом. Мост промолчал/сломался → считаем «отмена». */
export function askNativeConfirm(opts: {
  question: string;
  /** Текст-тело системного окна — поле message есть в контракте моста с первой сборки
   *  (native-app.ts, presentConfirm), поэтому строка правила 16 идёт им, а не приклейкой
   *  к заголовку; сборка без поля просто покажет заголовок. */
  message?: string;
  confirmLabel: string;
  cancelLabel: string;
  destructive: boolean;
}): Promise<boolean> {
  const br = nativeBridge();
  if (!br?.confirm || !nativeMethod('confirm')) return Promise.resolve(false);
  const accent = readAccentRgb();
  return br
    .confirm({
      question: opts.question,
      ...(opts.message ? { message: opts.message } : {}),
      confirmLabel: opts.confirmLabel,
      cancelLabel: opts.cancelLabel,
      destructive: opts.destructive,
      theme: document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
      ...accent,
    })
    .then((r) => !!r?.ok)
    .catch(() => false);
}
